import { Outlet } from 'react-router-dom';
import { useAppSelector } from '../../app/hooks';
import OnePager from '../../common/OnePager/OnePager';
import useLegacyBusinessDataFetch from '../../modules/DataRoom/useLegacyBusinessDataFetch';
import { createPortal } from 'react-dom';
import DashboardHeader from '../../modules/Dashboard/components/DashboardHeader/DashboardHeader';
import { useDispatch } from 'react-redux';
import { useFeatureVisibility } from '../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import {
    setShowUpgradeToScan,
    setShowUnlockAicaAndAddToLeads,
    updateNeedInfoFileNumber,
    setShowRefreshReport,
} from '../../store/investor/action';
import { ACCESS_TIERS, ACCESS_TYPES, DATA_POSITION, UnlockAICASourcePage, USER_TYPES } from '../../store/investor/constants';
import UnlockScanButton from '../../modules/Dashboard/DashboardModules/Overview/components/Buttons/UnlockScan/UnlockScanButton';
import AddToScanButton from '../../modules/Dashboard/DashboardModules/Overview/components/Buttons/AddToScan/AddToScanButton';
import { errorIconRed, pendingDataComplete, needInfoSelected } from '../../assets/hostedassets';
import clsx from 'clsx';
import { getDataGapDocs } from '../../common/MagicUpload/MagicUploadHelper';
import { useEffect, useRef } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Mixpanel } from '../../utils/mixpanel';
import { INVESTOR_OPEN_TAB } from '../../utils/constants/mixpanelEvents/investorEvents';
import styles from '../../scss/companyDashboardParent.module.scss';
import RefreshReport from '../../modules/Dashboard/components/RefreshReport/RefreshReport';
import { SOURCE } from '../../common/NeedInfoV2/NeedInfoV2Constants';
import EngagementLetter from '../../common/EngagementLetter/EngagementLetter';
import NDA from '../../common/NDA/NDA';
import ApplicationForm from '../../common/ApplicationForm/ApplicationForm';
import { getSubdomain } from '../../utils/utils';
import { AICA_DATA_PENDING } from '../../utils/constants/mixpanelEvents/investorEvents';
import { INVESTOR_ROUTES } from '../../utils/constants/routesConst';
import BureauKycConsent from '../../common/BureauKycConsent/BureauKycConsent';
import { REPORT_TAB_KEYS } from '../../utils/constants/commonConstants';
import { useIsMobile } from '../../common/_custom/Hooks/useIsMobile';

function CompanyDashboardParent() {
    const overviewOpenTabTrackedRef = useRef(false);
    const isMobile = useIsMobile();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch();
    const params = useParams();
    const header = document.getElementById('header') ?? document.createElement('div');

    const {
        exportData,
        isUserRestricted,
        selectedCompany,
        refreshReportStatus,
        downloadPDFStatus,
        goBackToPortfolio,
        onUnlockReportSuccess,
    } = useLegacyBusinessDataFetch();

    const { dataPendingEnabledVisibility } = useFeatureVisibility({
        dataPendingEnabledVisibility: VISIBILITY_KEYS.DATA_PENDING.ENABLED,
    });

    const user = useAppSelector((state) => state.user);
    const permissions = useAppSelector((state) => state.investor.permissions);
    const showDataGapCTA = permissions.showDataGap;

    const isScanLockedForUser = permissions?.[ACCESS_TIERS.SCAN] === ACCESS_TYPES.LOCKED;
    const isDemoProfile = !!selectedCompany?.demoOrg;
    const onePagerState = useAppSelector((state) => state.investor.onePager);
    const engagementLetterData = useAppSelector((state) => state.investor.engagementLetter);
    const bureauKycConsentData = useAppSelector((state) => state.investor.bureauAndKYCConsentLetter);
    const ndaData = useAppSelector((state) => state.investor.nda);
    const applicationFormData = useAppSelector((state) => state.investor.applicationForm);
    const showRefreshReport = useAppSelector((state) => state.investor.showRefreshReport);

    useEffect(() => {
        const isOnOverviewPage = location.pathname.includes('/portfolio');
        if (isOnOverviewPage && selectedCompany && user?.userData && !overviewOpenTabTrackedRef.current) {
            overviewOpenTabTrackedRef.current = true;
            const companyId = selectedCompany?.assesseeOrgId ?? selectedCompany?.orgId ?? params?.id ?? '';
            const companyName = selectedCompany?.orgName ?? selectedCompany?.organisationName ?? '';
            Mixpanel.track(INVESTOR_OPEN_TAB, {
                tab_name: REPORT_TAB_KEYS.OVERVIEW,
                user_id: user.userData.userId,
                user_type: user.userData.accountType,
                companyId,
                companyName,
            });
        }
    }, [selectedCompany, user]);

    // const getPendingDocumentsCount = async () => {
    //     await getDataGapDocs(
    //         {
    //             assesseeOrgId: params.id ?? '',
    //         },
    //         {
    //             onSuccess: (res) => {
    //                 dispatch(updateNeedInfoFileNumber(res.pendingCount));
    //             },
    //         },
    //     );
    // };
    //
    // useEffect(() => {
    //     getPendingDocumentsCount();
    // }, []);

    const openDataPendingDrawer = () => {
        searchParams.set('needInfo', 'true');
        searchParams.set('showNeedInfo', 'true');
        searchParams.set(SOURCE.BORROWER_REQ, 'true');
        // Only set requirementViewType to 'active' if it's not already set
        if (!searchParams.get('requirementViewType')) {
            searchParams.set('requirementViewType', 'active');
        }
        searchParams.set('companyNameInfo', selectedCompany?.orgName);
        // searchParams.set('readOnly', 'true');
        setSearchParams(searchParams);

        Mixpanel.track(AICA_DATA_PENDING, {
            userRole: user?.userData?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: user?.userData?.investorOrgId,
            userId: user?.userData?.userId,
            currentUrl: window.location.href,
            position: searchParams.get('type') === INVESTOR_ROUTES.SCAN ? DATA_POSITION.REPORT : searchParams.get('type'),
            organizationId: params.id,
        });
    };

    const renderHeaderButton = () => {
        if (!isUserRestricted) {
            if (isScanLockedForUser) {
                return <UnlockScanButton onClick={() => dispatch(setShowUpgradeToScan(ACCESS_TIERS.SCAN))} />;
            } else if (selectedCompany?.companyAccessType === ACCESS_TIERS.WATCH) {
                return (
                    <AddToScanButton
                        onClick={() => {
                            dispatch(
                                setShowUnlockAicaAndAddToLeads({
                                    source: UnlockAICASourcePage.DASHBOARD_HEADER,
                                    showModal: true,
                                }),
                            );
                        }}
                        accessType={selectedCompany?.companyAccessType}
                        isDemoProfile={isDemoProfile}
                    />
                );
            } else if (!isDemoProfile && dataPendingEnabledVisibility.show) {
                return (
                    <div className={styles.DataRequestCTA} onClick={openDataPendingDrawer}>
                        <img src={needInfoSelected} alt="Data Requests" />
                        <span>Data Requests</span>
                    </div>
                );
            }
        }
        return null;
    };

    return (
        <div>
            {!isMobile &&
                createPortal(
                    <DashboardHeader
                        exportData={exportData}
                        isUserRestricted={isUserRestricted}
                        selectedCompany={selectedCompany}
                        refreshReportStatus={refreshReportStatus}
                        downloadPDFStatus={downloadPDFStatus}
                        goBackToPortfolio={goBackToPortfolio}
                        renderHeaderButton={renderHeaderButton}
                    />,
                    header,
                )}
            <Outlet />
            {onePagerState.open && ['REPORT_TOP_BAR', 'PORTFOLIO'].includes(onePagerState.source as string) && <OnePager />}
            {engagementLetterData.open && ['REPORT_TOP_BAR', 'PORTFOLIO'].includes(engagementLetterData.source as string) && (
                <EngagementLetter />
            )}
            {bureauKycConsentData.open && ['REPORT_TOP_BAR', 'PORTFOLIO'].includes(bureauKycConsentData.source as string) && (
                <BureauKycConsent />
            )}
            {ndaData.open && ['REPORT_TOP_BAR', 'PORTFOLIO'].includes(ndaData.source as string) && <NDA />}
            {applicationFormData.open && ['REPORT_TOP_BAR', 'PORTFOLIO'].includes(applicationFormData.source as string) && (
                <ApplicationForm />
            )}

            {showRefreshReport && (
                <RefreshReport
                    selectedCompany={selectedCompany}
                    onUnlockReportSuccess={onUnlockReportSuccess}
                    close={() => {
                        dispatch(setShowRefreshReport(false));
                    }}
                />
            )}
        </div>
    );
}

export default CompanyDashboardParent;
